import { useState, useEffect, useCallback } from 'react'
import { evalApiClient } from '@/lib/api/eval.client'
import type { EvalCase } from '@super/shared-types'

interface UseEvalCasesResult {
  cases: EvalCase[]
  total: number
  limit: number
  offset: number
  isFetching: boolean
  error: string | null
  setOffset: (offset: number) => void
  refetch: () => void
}

export function useEvalCases(
  opts: {
    companyId?: string
    promptType?: string
    limit?: number
  } = {},
): UseEvalCasesResult {
  const { companyId, promptType, limit = 50 } = opts
  const [cases, setCases] = useState<EvalCase[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fetchTick, setFetchTick] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsFetching(true)
    setError(null)

    evalApiClient
      .getCases({ companyId, promptType, limit, offset })
      .then(response => {
        if (cancelled) return
        if (response) {
          setCases(response.data)
          setTotal(response.pagination.total)
        } else {
          setError('Failed to load eval cases')
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })

    return () => {
      cancelled = true
    }
  }, [companyId, promptType, limit, offset, fetchTick])

  const refetch = useCallback(() => setFetchTick(t => t + 1), [])

  return { cases, total, limit, offset, isFetching, error, setOffset, refetch }
}
